import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { RoleEditSchema } from "../../../ValidationSchema";
import ReactSelect from "../../../Components/PortalSelect";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const colourStyles = {
  control: (styles) => ({
    ...styles,
    border: "1px solid #bfc9d4",
    boxShadow: "#000 !important",
    borderRadius: "6px",
    padding: "4px",
    "&:hover": {
      border: "1px solid #bfc9d4",
    },
  }),
  multiValue: (styles) => {
    return {
      ...styles,
      backgroundColor: "#606060",
    };
  },
  multiValueLabel: (styles) => ({
    ...styles,
    color: "#fff",
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: "#000",
    "& > svg": {
      backgroundColor: "#fff",
      borderRadius: "10px",
      marginLeft: "2px",
      marginRight: "2px",
    },
    ":hover": {
      backgroundColor: data.color,
      color: data.color,
    },
  }),
  option: (styles, { data }) => {
    return {
      ...styles,
      backgroundColor: "white",
      color: "#000",
      ":hover": {
        backgroundColor: "#f5f5f5",
      },
    };
  },
};

const BlogEdit = () => {
  const location = useLocation();
  const Navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    title: "",
    author_name: "",
    tags: "",
    author_image: null,
    description: "",
    category: "",
    image_path: null,
  });

  const [categoryData, setCategoryData] = useState([]);

  const CategoriesData = () => {
    axios
      .get(`${process.env.REACT_APP_URL}/admin/category`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {
        setCategoryData(
          res?.data?.data?.categories.map((item) => {
            return {
              label: item?.product_category_name || "Untitled", // fallback if title is undefined
              value: item?.product_category_id,
            };
          })
        );
      })
      .catch((err) => {
        if (err.response.data.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  const GetUserData = () => {
    axios
      .get(`${process.env.REACT_APP_URL}/admin/blog/${location?.state?.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {
        if (res.status === 200) {
          setInitialValues({
            title: res?.data?.data?.title,
            tags: res?.data?.data?.tags,
            description: res?.data?.data?.description,
            category: res?.data?.data?.category,
            date_time: res?.data?.data?.date_time,
            author_name: res?.data?.data?.author_name,
            author_image: res?.data?.data?.author_image,
            image_path: res?.data?.data?.image_path,
          });
        }
      })
      .catch((err) => {

        const MSG = ["Token not found.", "Invalid token.", "Unauthorized."];
        const STATUS = [401, 403];
        if (
          STATUS.includes(err.response.status) ||
          MSG.includes(err.response.data.msg)
        ) {
          localStorage.clear();
          Navigate("/admin/login");
        } else {
          toast.error(err?.response?.data?.msg);
        }
      });
  };

  const EditBlog = useFormik({
    initialValues,
    enableReinitialize: true,

    onSubmit: (values, action) => {
      const formData = new FormData();

      // Text fields
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("category", values.category);
      formData.append("author_name", values.author_name);
      formData.append("date_time", values.date_time);
      formData.append("tags", values.tags);
      formData.append("is_active", values.is_active ?? true); // default true if not provided

      // File fields → only append if new file uploaded
      if (values.image_path instanceof File) {
        formData.append("image_path", values.image_path);
      }
      if (values.author_image instanceof File) {
        formData.append("author_image", values.author_image);
      }

      axios
        .put(
          `${process.env.REACT_APP_URL}/admin/blog/${location?.state?.id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then((res) => {
          if (res.status === 200) {
            toast.success("Blog Updated Successfully");
            Navigate("/admin/blog", { state: res.data.msg });
          }
        })
        .catch((err) => {
          toast.error(err.response?.data?.message || "Update failed");
        });
    },
  });

  //   const UserRole = () => {
  //     axios
  //       .get(`${process.env.REACT_APP_URL}/common/roles`)
  //       .then((res) => {
  //         setUserRole(res.data.roles);
  //       })
  //       .catch((err) => {
  //         if (err.response.data.message === "Unauthorized!") {
  //           Navigate("/admin/login");
  //         }
  //       });
  //   };

  useEffect(() => {
    GetUserData();
    CategoriesData();
  }, []);

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Edit Blog</h1>
          <p className="al-sub">Update this blog post.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <form onSubmit={EditBlog.handleSubmit}>
        <div className="al-card">
          <div className="al-cardhead">
            <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg></div>
            <h3 className="al-cardhead__title">Blog Details</h3>
          </div>

          <div className="al-form">
            {/* Title */}
            <div>
              <label className="al-label">Title</label>
              <input className="al-input" type="text" placeholder="Enter Title"
                value={EditBlog.values.title} onChange={EditBlog.handleChange} onBlur={EditBlog.handleBlur} name="title" />
              {EditBlog.errors.title && EditBlog.touched.title && (
                <div className="al-err">{EditBlog.errors.title}</div>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="al-label">Category</label>
              <ReactSelect
                options={categoryData}
                isClearable={false}
                styles={colourStyles}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                placeholder="Select Category"
                name="category"
                onChange={(option) => EditBlog.setFieldValue("category", option ? option.label : "")}
                value={categoryData.find((cat) => cat.label === EditBlog.values.category) || null}
              />
              {EditBlog.errors.category && EditBlog.touched.category && (
                <div className="al-err">{EditBlog.errors.category}</div>
              )}
            </div>

            {/* Author Name */}
            <div>
              <label className="al-label">Author Name</label>
              <input className="al-input" type="text" placeholder="Enter Author Name"
                value={EditBlog.values.author_name} onChange={EditBlog.handleChange} onBlur={EditBlog.handleBlur} name="author_name" />
              {EditBlog.errors.author_name && EditBlog.touched.author_name && (
                <div className="al-err">{EditBlog.errors.author_name}</div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="al-label">Tags</label>
              <input className="al-input" type="text" placeholder="Enter Tags"
                value={EditBlog.values.tags} onChange={EditBlog.handleChange} onBlur={EditBlog.handleBlur} name="tags" />
              {EditBlog.errors.tags && EditBlog.touched.tags && (
                <div className="al-err">{EditBlog.errors.tags}</div>
              )}
            </div>

            {/* Author Image */}
            <div>
              <label className="al-label">Author Image</label>
              <input className="al-input al-input--file" type="file" name="author_image" accept="image/*"
                onChange={(e) => EditBlog.setFieldValue("author_image", e.target.files && e.target.files[0])}
                onBlur={EditBlog.handleBlur} />
              {EditBlog.errors.author_image && EditBlog.touched.author_image && (
                <div className="al-err">{EditBlog.errors.author_image}</div>
              )}
              <div className="mt-2">
                {EditBlog.values.author_image && typeof EditBlog.values.author_image === "string" && (
                  <img src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${EditBlog.values.author_image}`} alt="Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                )}
                {EditBlog.values.author_image && typeof EditBlog.values.author_image === "object" && (
                  <img
                    src={
                      EditBlog.values.author_image instanceof File
                        ? URL.createObjectURL(EditBlog.values.author_image)
                        : `${process.env.REACT_APP_IMAGE_URL}/uploads/${EditBlog.values.author_image}`
                    }
                    alt="New Upload Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                )}
              </div>
            </div>

            {/* Picture */}
            <div>
              <label className="al-label">Picture</label>
              <input className="al-input al-input--file" type="file" name="image_path" accept="image/*"
                onChange={(e) => EditBlog.setFieldValue("image_path", e.target.files && e.target.files[0])}
                onBlur={EditBlog.handleBlur} />
              {EditBlog.errors.image_path && EditBlog.touched.image_path && (
                <div className="al-err">{EditBlog.errors.image_path}</div>
              )}
              <div className="mt-2">
                {EditBlog.values.image_path && typeof EditBlog.values.image_path === "string" && (
                  <img src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${EditBlog.values.image_path}`} alt="Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                )}
                {EditBlog.values.image_path && typeof EditBlog.values.image_path === "object" && (
                  <img src={URL.createObjectURL(EditBlog.values.image_path)} alt="New Upload Preview"
                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                )}
              </div>
            </div>

            {/* Date */}
            <div>
              <label className="al-label">Date</label>
              <input className="al-input" type="date" placeholder="Enter Date"
                value={EditBlog.values.date_time} onChange={EditBlog.handleChange} onBlur={EditBlog.handleBlur} name="date_time" />
              {EditBlog.errors.date_time && EditBlog.touched.date_time && (
                <div className="al-err">{EditBlog.errors.date_time}</div>
              )}
            </div>

            {/* Description (full width) */}
            <div className="al-form__full">
              <label className="al-label">Description</label>
              {EditBlog.values && (
                <CKEditor
                  key={EditBlog.values.title || "editor"}
                  editor={ClassicEditor}
                  data={EditBlog.values.description || ""}
                  onChange={(event, editor) => EditBlog.setFieldValue("description", editor.getData())}
                  onBlur={() => EditBlog.setFieldTouched("description", true)}
                />
              )}
              {EditBlog.errors.description && EditBlog.touched.description && (
                <div className="al-err">{EditBlog.errors.description}</div>
              )}
            </div>
          </div>

          <div className="al-actionbar">
            <button type="button" className="al-btn" onClick={() => Navigate(-1)}>Cancel</button>
            <button type="submit" className="al-btn al-btn--primary">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              Save Changes
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BlogEdit;
